// packages/web-ui/src/components/welcome/UserDocsModal.tsx
//
// User-facing in-app documentation modal. Opened via the ActivityBar Help
// button's "Open docs" item (which dispatches `tessa:openDocs`) or
// programmatically. Mirrors the modal shell of `<KeyboardCheatSheet>` —
// backdrop click + Esc dismiss, focus-trap on Tab, brand-aligned header.
//
// Content audience: systems / mechanical engineers, NOT developers. Avoid
// jargon (entities, pipelines, deltas, applier, decompose-translate-assemble);
// use concrete examples and verb-led instructions ("Type this", "Click that").

import type { JSX, ComponentType } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import {
  MessageSquare,
  Network,
  FolderOpen,
  LayoutGrid,
  GitCompare,
  Zap,
  Settings,
} from 'lucide-react';
import { CHEAT_SHEET_OPEN_EVENT } from '../../hooks/useKeymap.js';
import { useTutorialStore } from '../../state/tutorial.js';

/** Window-level custom event consumed by `<AppShell>` to open this modal. */
export const OPEN_DOCS_EVENT = 'tessa:openDocs';

/** Vite injects the package.json version via `import.meta.env.PACKAGE_VERSION`
 *  (or falls back to a static string when the env var isn't wired). */
function getVersion(): string {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return env?.PACKAGE_VERSION ?? '0.0.1';
}

export interface UserDocsModalProps {
  isOpen: boolean;
  onClose: () => void;
  testId?: string;
}

// ─── Try-it-now examples — drawn from _bmad-output/planning-artifacts/use-cases.md ──
interface ExamplePrompt {
  id: string;
  prompt: string;
  outcome: string;
  kind: 'diagram' | 'change';
}

const EXAMPLES: ReadonlyArray<ExamplePrompt> = [
  {
    id: 'context',
    prompt: 'Show me the context of BrakeSystem — what is it connected to today?',
    outcome:
      'Draws a diagram of BrakeSystem with its ports and every part it touches. Nothing in your model changes.',
    kind: 'diagram',
  },
  {
    id: 'weight',
    prompt: 'Calculate the total mass of Vehicle and show every component with its mass.',
    outcome:
      'Sums weight across every part and shows a tree with the heaviest item highlighted. Nothing in your model changes.',
    kind: 'diagram',
  },
  {
    id: 'change',
    prompt:
      'Add a regenerative-braking signal connection from BrakeSystem to MotorController.',
    outcome:
      'Proposes a new port on each part and a connection between them. You review and click ✓ or ✗ on each one before anything is saved.',
    kind: 'change',
  },
  {
    id: 'trace',
    prompt: 'Trace the event chain for brakePedalInput starting from BrakeSystem.',
    outcome:
      'Follows the signal hop-by-hop and draws the chain. Useful for understanding what reacts to what.',
    kind: 'diagram',
  },
];

// ─── Panels (Activity Bar destinations) ────────────────────────────────────
interface DestinationDoc {
  key: string;
  label: string;
  shortcut: string;
  Icon: ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;
  blurb: string;
}

const DESTINATIONS: ReadonlyArray<DestinationDoc> = [
  {
    key: 'chat',
    label: 'Chat',
    shortcut: 'Ctrl+1',
    Icon: MessageSquare,
    blurb:
      'Where you talk to the assistant in plain English. Type a question or describe a change and press Enter.',
  },
  {
    key: 'kb',
    label: 'KB Browser',
    shortcut: 'Ctrl+2',
    Icon: Network,
    blurb:
      'Browse your model the way a librarian would — find parts, see their ports, follow connections. Click any item to open its card.',
  },
  {
    key: 'files',
    label: 'Files',
    shortcut: 'Ctrl+3',
    Icon: FolderOpen,
    blurb:
      'See and edit the raw .sysml files behind your model. Most of the time you do not need to open this — the assistant writes the files for you.',
  },
  {
    key: 'views',
    label: 'Views',
    shortcut: 'Ctrl+4',
    Icon: LayoutGrid,
    blurb:
      'All the diagrams the assistant has drawn for you, saved as reusable views. Open one to edit the layout or download it as an image (SVG).',
  },
  {
    key: 'diff',
    label: 'Diff',
    shortcut: 'Ctrl+5',
    Icon: GitCompare,
    blurb:
      'The review board for proposed changes. Accept or reject one row at a time. Past change sessions are kept here too, read-only.',
  },
  {
    key: 'skills',
    label: 'Skills',
    shortcut: 'Ctrl+6',
    Icon: Zap,
    blurb:
      'Reusable instructions that tell the assistant how to handle a recurring kind of question (e.g. "trace this signal", "sum these masses"). Add your own here.',
  },
  {
    key: 'settings',
    label: 'Settings',
    shortcut: 'Ctrl+,',
    Icon: Settings,
    blurb:
      'Configure your LLM provider key, the model used, and UI preferences. Most of these are set once by an admin and forgotten.',
  },
];

// ─── Skills shipped with the demo workspace ───────────────────────────────
interface SkillDoc {
  name: string;
  oneLiner: string;
}

const SHIPPED_SKILLS: ReadonlyArray<SkillDoc> = [
  {
    name: 'context-analysis',
    oneLiner: 'Show one part with its ports and direct neighbours — answers "what is X connected to?"',
  },
  {
    name: 'hierarchy-listing',
    oneLiner: 'List the components, children, or sub-parts of a system.',
  },
  {
    name: 'traceability',
    oneLiner: 'Follow a connection or signal across multiple hops — answers "trace path from A to B".',
  },
  {
    name: 'budget-aggregation',
    oneLiner: 'Total or break down a quantity (weight, power, cost) across a tree of parts.',
  },
  {
    name: 'weight-breakdown',
    oneLiner: 'Recursive weight roll-up shown as an indented tree with subtotals at every level.',
  },
  {
    name: 'bom-cost',
    oneLiner: 'Bill-of-materials cost roll-up with actual-vs-target variance per component.',
  },
  {
    name: 'powertrain-efficiency',
    oneLiner: 'Walks Battery → Motor, multiplies stage efficiencies, derives a range estimate.',
  },
  {
    name: 'safety-analysis',
    oneLiner: 'Walks safety chains and composes worst-case ASIL ratings across connected parts.',
  },
  {
    name: 'governance-recommendation',
    oneLiner: 'Looks up who owns a part and who needs to approve a proposed change.',
  },
  {
    name: 'reference-resolution',
    oneLiner: 'Resolves the names you mention against the model before proposing a change.',
  },
  {
    name: 'read-from-input',
    oneLiner: 'Reads a meeting-notes or change-request document and routes each action item.',
  },
];

// ─── Tips & shortcuts ──────────────────────────────────────────────────────
interface TipItem {
  title: string;
  body: string;
}

const TIPS: ReadonlyArray<TipItem> = [
  {
    title: 'Capability chips (top of Chat)',
    body:
      'Small chips show what is wired up in your workspace: Git, KB (model loaded), DA (domain agents), Sub (sub-agents), Skills. The Tier label sums it up — basic, standard, or rich. More chips lit = richer answers.',
  },
  {
    title: 'The 5 attributes you can track',
    body:
      'Each part can carry weight (kg), energy (Wh), power (W), cost (EUR), and efficiency. These are standardized so the assistant can sum them across systems. Other attributes (engineer name, ASIL rating, etc.) are kept on port and connection definitions.',
  },
  {
    title: 'Useful chat shortcuts',
    body:
      'Type @ to mention a specific part by name. Type / to see slash commands like /new (new session), /clear (clear chat). Press the up arrow on an empty chat to bring back your last message.',
  },
  {
    title: 'Your files are safe',
    body:
      'Changes are never written until you click ✓ on a proposed row. Diagrams (Views) are saved separately under .tessa-lean/views/ and never touch your model files.',
  },
];

// ─── Troubleshooting — the most common "why doesn't this work?" cases ─────
interface FixIt {
  symptom: string;
  cause: string;
}

const TROUBLESHOOT: ReadonlyArray<FixIt> = [
  {
    symptom: 'The KB chip shows ✗ and the assistant says it cannot find anything.',
    cause:
      'The open folder does not have a tessa-config.json file. Ask your admin to set up the workspace, or open the folder that contains your model.',
  },
  {
    symptom: 'A chip stays grey (e.g. DA ✗ or Skills 0).',
    cause:
      'That capability is not configured in this workspace. The app still works — answers will use only what is available.',
  },
  {
    symptom: 'I accepted a change but nothing seems to happen.',
    cause:
      'Files are written in the background. Open the Files tab to see them, or the Views tab to see the diagram refresh.',
  },
  {
    symptom: 'The assistant refused to add an attribute I asked for.',
    cause:
      'KB-level attributes are restricted to weight, energy, power, cost, and efficiency. Other values can live on port or connection definitions — phrase your request that way.',
  },
];

export function UserDocsModal({
  isOpen,
  onClose,
  testId = 'user-docs-modal',
}: UserDocsModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const startTour = useTutorialStore((s) => s.startTour);

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.focus();
    }
  }, [isOpen]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (root === null) return;
      const focusable = root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  const onOpenShortcuts = useCallback(() => {
    onClose();
    window.dispatchEvent(new CustomEvent(CHEAT_SHEET_OPEN_EVENT));
  }, [onClose]);

  const onStartTutorial = useCallback(() => {
    onClose();
    startTour();
  }, [onClose, startTour]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testId}-title`}
      data-testid={testId}
      onKeyDown={onKeyDown}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-[2100] flex items-center justify-center bg-black/60 p-4"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        data-testid={`${testId}-panel`}
        className="bg-cream-50 border-taupe-500 flex max-h-[92vh] w-full max-w-4xl flex-col gap-7 overflow-y-auto rounded-none border p-8 outline-none"
      >
        {/* ─── Header ─────────────────────────────────────────────────── */}
        <header className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-2">
            <span className="text-audi-red text-[11px] font-semibold uppercase tracking-[0.22em]">
              User guide
            </span>
            <h2
              id={`${testId}-title`}
              className="text-fg text-2xl font-extrabold tracking-[-0.01em]"
            >
              Welcome to TESSA Lean
            </h2>
            <p className="text-taupe-700 max-w-2xl text-sm leading-relaxed">
              TESSA Lean helps you{' '}
              <strong className="text-fg">explore</strong> and{' '}
              <strong className="text-fg">change</strong> a SysML v2 systems
              model by chatting in plain English. The assistant either{' '}
              <em>draws a diagram</em> to answer your question, or{' '}
              <em>proposes a change</em> you review one row at a time. Nothing
              is written to your files until you say so.
            </p>
          </div>
          <button
            type="button"
            data-testid={`${testId}-close`}
            onClick={onClose}
            aria-label="Close user guide"
            className="border-taupe-500 hover:border-taupe-900 hover:bg-cream-100 shrink-0 rounded-none border px-3 py-1 text-[11px] uppercase tracking-wide"
          >
            Close
          </button>
        </header>

        {/* ─── Try it now ─────────────────────────────────────────────── */}
        <section
          data-testid={`${testId}-section-examples`}
          aria-labelledby={`${testId}-examples-title`}
          className="flex flex-col gap-3"
        >
          <h3
            id={`${testId}-examples-title`}
            className="text-fg border-taupe-300 border-b pb-1 text-base font-semibold"
          >
            Try it now — example prompts
          </h3>
          <p className="text-taupe-700 text-xs leading-relaxed">
            Open the <strong>Chat</strong> tab and paste any of these. Swap{' '}
            <code className="bg-cream-100 border-taupe-300 rounded-none border px-1 text-[11px]">
              BrakeSystem
            </code>{' '}
            /{' '}
            <code className="bg-cream-100 border-taupe-300 rounded-none border px-1 text-[11px]">
              Vehicle
            </code>{' '}
            for whatever parts exist in your own model.
          </p>
          <ul className="flex flex-col gap-2">
            {EXAMPLES.map((ex) => (
              <li
                key={ex.id}
                data-testid={`${testId}-example-${ex.id}`}
                className="border-taupe-300 bg-cream-50 flex flex-col gap-1 border p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <code className="text-fg text-xs leading-relaxed">
                    &ldquo;{ex.prompt}&rdquo;
                  </code>
                  <span
                    data-kind={ex.kind}
                    className={
                      ex.kind === 'change'
                        ? 'bg-audi-red shrink-0 rounded-none px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white'
                        : 'border-taupe-500 text-taupe-700 shrink-0 rounded-none border bg-transparent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide'
                    }
                  >
                    {ex.kind === 'change' ? 'Proposes a change' : 'Just a diagram'}
                  </span>
                </div>
                <span className="text-taupe-700 text-xs leading-relaxed">
                  {ex.outcome}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* ─── The two main flows ─────────────────────────────────────── */}
        <section
          data-testid={`${testId}-section-flows`}
          aria-labelledby={`${testId}-flows-title`}
          className="flex flex-col gap-3"
        >
          <h3
            id={`${testId}-flows-title`}
            className="text-fg border-taupe-300 border-b pb-1 text-base font-semibold"
          >
            The two things TESSA Lean does
          </h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div
              data-testid={`${testId}-flow-diagram`}
              className="border-taupe-300 flex flex-col gap-1.5 border p-3"
            >
              <span className="text-fg text-sm font-semibold">
                1. Draws a diagram (no changes)
              </span>
              <span className="text-taupe-700 text-xs leading-relaxed">
                When you ask <em>&ldquo;show me…&rdquo;</em>, <em>&ldquo;trace…&rdquo;</em>, or{' '}
                <em>&ldquo;calculate…&rdquo;</em>, the assistant generates a diagram and
                shows it as a preview card in chat. The diagram is also saved
                under <strong>Views</strong> so you can reopen, edit the
                layout, or export it as an image. Your model files are
                untouched.
              </span>
            </div>
            <div
              data-testid={`${testId}-flow-change`}
              className="border-audi-red flex flex-col gap-1.5 border p-3"
            >
              <span className="text-fg text-sm font-semibold">
                2. Proposes a change (you review)
              </span>
              <span className="text-taupe-700 text-xs leading-relaxed">
                When you ask <em>&ldquo;add…&rdquo;</em>, <em>&ldquo;remove…&rdquo;</em>, or{' '}
                <em>&ldquo;rename…&rdquo;</em>, the assistant shows a list of every
                affected part, port, and connection. Click{' '}
                <strong>✓ accept</strong> or <strong>✗ reject</strong> on each
                row. Only accepted rows are saved to your .sysml files.
                Everything appears in the <strong>Diff</strong> tab too.
              </span>
            </div>
          </div>
        </section>

        {/* ─── What each panel does ───────────────────────────────────── */}
        <section
          data-testid={`${testId}-section-destinations`}
          aria-labelledby={`${testId}-destinations-title`}
          className="flex flex-col gap-3"
        >
          <h3
            id={`${testId}-destinations-title`}
            className="text-fg border-taupe-300 border-b pb-1 text-base font-semibold"
          >
            What each panel does
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {DESTINATIONS.map((d) => (
              <div
                key={d.key}
                data-testid={`${testId}-destination-${d.key}`}
                className="border-taupe-300 bg-cream-50 hover:border-taupe-500 flex gap-3 border p-3"
              >
                <div
                  aria-hidden="true"
                  className="text-fg bg-cream-100 flex h-9 w-9 shrink-0 items-center justify-center"
                >
                  <d.Icon size={18} aria-hidden={true} />
                </div>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-fg text-sm font-semibold">
                      {d.label}
                    </span>
                    <kbd className="border-taupe-300 bg-cream-100 text-taupe-700 rounded-none border px-1.5 py-0.5 font-mono text-[10px]">
                      {d.shortcut}
                    </kbd>
                  </div>
                  <span className="text-taupe-700 text-xs leading-relaxed">
                    {d.blurb}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── Skills ─────────────────────────────────────────────────── */}
        <section
          data-testid={`${testId}-section-skills`}
          aria-labelledby={`${testId}-skills-title`}
          className="flex flex-col gap-3"
        >
          <h3
            id={`${testId}-skills-title`}
            className="text-fg border-taupe-300 border-b pb-1 text-base font-semibold"
          >
            Skills — the assistant&rsquo;s playbook
          </h3>
          <p className="text-taupe-700 text-xs leading-relaxed">
            A <strong>skill</strong> is a short written playbook that tells the
            assistant how to handle a recurring kind of question. The assistant
            picks the right skill automatically based on what you typed. Each
            skill lives in a file under{' '}
            <code className="bg-cream-100 border-taupe-300 rounded-none border px-1 text-[11px]">
              .tessa-lean/skills/&lt;name&gt;/SKILL.md
            </code>{' '}
            inside your workspace.
          </p>

          <div className="flex flex-col gap-1.5">
            <span
              data-testid={`${testId}-skills-shipped-title`}
              className="text-fg text-xs font-semibold uppercase tracking-wide"
            >
              Skills shipped with the demo workspace
            </span>
            <ul className="grid grid-cols-1 gap-1 md:grid-cols-2">
              {SHIPPED_SKILLS.map((sk) => (
                <li
                  key={sk.name}
                  data-testid={`${testId}-skill-${sk.name}`}
                  className="flex flex-col gap-0.5"
                >
                  <code className="text-fg text-[11px] font-semibold">
                    {sk.name}
                  </code>
                  <span className="text-taupe-700 text-xs leading-relaxed">
                    {sk.oneLiner}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-taupe-700 mt-1 text-[11px] italic leading-relaxed">
              The Skills tab (<kbd className="border-taupe-300 bg-cream-100 rounded-none border px-1 py-0.5 font-mono text-[10px]">Ctrl+6</kbd>) shows
              which skills are loaded in <em>your</em> workspace right now —
              that may differ from the demo set above.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <span
              data-testid={`${testId}-skills-create-title`}
              className="text-fg text-xs font-semibold uppercase tracking-wide"
            >
              Add your own skill in 4 steps
            </span>
            <ol className="text-taupe-700 ml-4 flex list-decimal flex-col gap-1 text-xs leading-relaxed">
              <li>
                Go to the <strong>Skills</strong> tab (or press{' '}
                <kbd className="border-taupe-300 bg-cream-100 rounded-none border px-1 py-0.5 font-mono text-[10px]">
                  Ctrl+6
                </kbd>
                ).
              </li>
              <li>
                Click the <strong>+</strong> button and give your skill a
                lower-case-with-hyphens name (e.g.{' '}
                <code className="bg-cream-100 border-taupe-300 rounded-none border px-1 text-[11px]">
                  failure-mode-analysis
                </code>
                ).
              </li>
              <li>
                Fill in <strong>Description</strong> (what the skill does) and{' '}
                <strong>When to use</strong> (the kinds of phrasings that
                should trigger it — list real example questions). Write the{' '}
                <strong>Body</strong> as numbered steps the assistant should
                follow.
              </li>
              <li>
                Press <strong>Ctrl+S</strong> to save. The new skill is picked
                up immediately — no restart, no config change.
              </li>
            </ol>
            <p className="text-taupe-700 mt-1 text-[11px] italic leading-relaxed">
              Tip: Open one of the demo skills above and use{' '}
              <strong>Save as</strong> as a starting template. Good skills
              specify exactly which tools the assistant must call before
              answering, and ban inventing facts.
            </p>
          </div>
        </section>

        {/* ─── Tips ───────────────────────────────────────────────────── */}
        <section
          data-testid={`${testId}-section-tips`}
          aria-labelledby={`${testId}-tips-title`}
          className="flex flex-col gap-3"
        >
          <h3
            id={`${testId}-tips-title`}
            className="text-fg border-taupe-300 border-b pb-1 text-base font-semibold"
          >
            Tips worth knowing
          </h3>
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {TIPS.map((tip) => (
              <li
                key={tip.title}
                data-testid={`${testId}-tip-${tip.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="flex flex-col gap-0.5"
              >
                <span className="text-fg text-xs font-semibold uppercase tracking-wide">
                  {tip.title}
                </span>
                <span className="text-taupe-700 text-xs leading-relaxed">
                  {tip.body}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* ─── Troubleshooting ─────────────────────────────────────────── */}
        <section
          data-testid={`${testId}-section-troubleshoot`}
          aria-labelledby={`${testId}-troubleshoot-title`}
          className="flex flex-col gap-3"
        >
          <h3
            id={`${testId}-troubleshoot-title`}
            className="text-fg border-taupe-300 border-b pb-1 text-base font-semibold"
          >
            If something feels off
          </h3>
          <ul className="flex flex-col gap-2">
            {TROUBLESHOOT.map((fix, i) => (
              <li
                key={i}
                data-testid={`${testId}-fix-${i}`}
                className="flex flex-col gap-0.5"
              >
                <span className="text-fg text-xs font-semibold">
                  {fix.symptom}
                </span>
                <span className="text-taupe-700 text-xs leading-relaxed">
                  {fix.cause}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* ─── Footer ─────────────────────────────────────────────────── */}
        <footer
          data-testid={`${testId}-footer`}
          className="border-taupe-300 flex flex-wrap items-center justify-between gap-3 border-t pt-4 text-xs"
        >
          <div className="text-taupe-700 flex flex-col gap-0.5">
            <span>
              Press{' '}
              <kbd className="border-taupe-300 bg-cream-100 rounded-none border px-1 py-0.5 font-mono text-[10px]">
                F1
              </kbd>{' '}
              anywhere to reopen this guide.
            </span>
            <span
              data-testid={`${testId}-about`}
              className="text-taupe-700 text-[11px]"
            >
              TESSA Lean{' '}
              <span data-testid={`${testId}-version`}>v{getVersion()}</span>
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid={`${testId}-start-tutorial`}
              onClick={onStartTutorial}
              className="bg-audi-red hover:bg-audi-red/90 rounded-none px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white"
            >
              Start guided tutorial
            </button>
            <button
              type="button"
              data-testid={`${testId}-open-shortcuts`}
              onClick={onOpenShortcuts}
              className="border-taupe-500 hover:border-taupe-900 hover:bg-cream-100 rounded-none border px-3 py-1 text-[11px] uppercase tracking-wide"
            >
              Keyboard shortcuts
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
